// Considered: MessageAtom, AlertCardMolecule, ConfirmDialogAtom — rejected (spec Reuse Audit):
// MessageAtom cannot hold an action row without a wrapping atom of buttons and PrimeReact gives
// it role="alert"; AlertCardMolecule is a Card with a banner and gradient and has no danger tone;
// ConfirmDialogAtom is modal, which both the design and the "rail for every add and edit" surface
// decision forbid. Source: the prototype's `p-ask` (studio.css:640-643).
//
// An inline, non-modal confirmation: what is happening, what it affects, and the actions —
// rendered in place rather than over a scrim. Used for detach, discard-all and (later) any other
// consequential action inside the rail that must not interrupt the page with a dialog.

import React from 'react';
import ButtonAtom, { type ButtonVariant } from '../../atoms/Button/ButtonAtom';
import './InlineConfirmMolecule.scss';

export type InlineConfirmTone = 'warning' | 'danger';

export interface InlineConfirmAction {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  'data-testid'?: string;
  'data-datum'?: string;
}

export interface InlineConfirmMoleculeProps {
  tone?: InlineConfirmTone;
  icon?: string;
  /** id placed on the title — also used as the group's `aria-labelledby` target. */
  titleId: string;
  title: React.ReactNode;
  titleTestId?: string;
  titleDatum?: string;
  body: React.ReactNode;
  bodyTestId?: string;
  bodyDatum?: string;
  /** Extra content between the body and the actions — e.g. a labelled input the ask needs before it can proceed. */
  children?: React.ReactNode;
  actions: InlineConfirmAction[];
  className?: string;
  testId?: string;
  datum?: string;
}

const InlineConfirmMolecule: React.FC<InlineConfirmMoleculeProps> = ({
  tone = 'warning',
  icon,
  titleId,
  title,
  titleTestId,
  titleDatum,
  body,
  bodyTestId,
  bodyDatum,
  children,
  actions,
  className = '',
  testId,
  datum,
}) => {
  return (
    <div
      className={['inline-confirm', `inline-confirm--tone-${tone}`, className].filter(Boolean).join(' ')}
      role="group"
      aria-labelledby={titleId}
      data-testid={testId}
      data-datum={datum}
      data-kind="callout"
    >
      <p className="inline-confirm__head">
        {icon && <i className={`pi ${icon}`} aria-hidden="true" />}
        <b id={titleId} className="inline-confirm__title" data-testid={titleTestId} data-datum={titleDatum} data-kind="strong">
          {title}
        </b>
      </p>
      <p className="inline-confirm__body" data-testid={bodyTestId} data-datum={bodyDatum} data-kind="hint">
        {body}
      </p>
      {children}
      <div className="inline-confirm__acts">
        {actions.map((action) => (
          <ButtonAtom
            key={action.label}
            variant={action.variant ?? 'text'}
            size="small"
            label={action.label}
            onClick={action.onClick}
            disabled={action.disabled}
            data-testid={action['data-testid']}
            data-datum={action['data-datum']}
            data-kind="button"
          />
        ))}
      </div>
    </div>
  );
};

InlineConfirmMolecule.displayName = 'InlineConfirmMolecule';

export default InlineConfirmMolecule;
